import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Users, Download, Phone, Music, Loader2, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * GuestsPage — venue admin view of all customer scans.
 *
 * A "scan" = a verified OTP for a session belonging to this venue.
 * Rows are deduped per phone number so repeat visitors appear once with
 * an incremented `scan_count`. Also displays how many song requests they
 * placed (approved + pending, excludes rejected).
 *
 * The venue can export the full list to CSV for offline analysis /
 * marketing (WhatsApp broadcasts, loyalty programmes, etc.).
 */
const RANGE_OPTIONS = [
  { label: 'This week',  value: { range: 'this_week' } },
  { label: 'This month', value: { range: 'this_month' } },
  { label: 'Last week',  value: { range: 'last_week' } },
  { label: 'Last month', value: { range: 'last_month' } },
  { label: '90 days',    value: { days: 90 } },
  { label: 'All time',   value: {} },
];

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      day: 'numeric', month: 'short', year: '2-digit',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
};

const GuestsPage = () => {
  const [rangeIdx, setRangeIdx] = useState(1); // default: This month
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [data, setData] = useState({ guests: [], total: 0, verified_count: 0, lead_count: 0, total_scans: 0, total_requests: 0 });

  const currentParams = RANGE_OPTIONS[rangeIdx].value;

  const fetchGuests = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/analytics/guests`, { params: currentParams, withCredentials: true });
      setData(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load guests');
    } finally {
      setLoading(false);
    }
  }, [currentParams]);

  useEffect(() => { fetchGuests(); }, [fetchGuests]);

  const downloadCsv = async () => {
    setDownloading(true);
    try {
      const res = await axios.get(`${API}/analytics/guests/csv`, {
        params: currentParams,
        withCredentials: true,
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      const stamp = new Date().toISOString().slice(0, 10);
      // Match the backend's Content-Disposition slug so the downloaded
      // filename tells the venue which window they exported.
      const slug = currentParams.range
        || (currentParams.days ? `${currentParams.days}d` : 'alltime');
      link.setAttribute('download', `guests-${slug}-${stamp}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('CSV downloaded');
    } catch (err) {
      toast.error('Download failed');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Layout>
      <div className="p-4 md:p-6 lg:p-8 pb-32 overflow-y-auto content-scroll" data-testid="guests-page">
        {/* Header */}
        <div className="mb-6 md:mb-10">
          <div className="flex items-center gap-3 mb-2">
            <Users className="w-6 h-6 md:w-8 md:h-8 text-[#FF007F]" />
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-white font-['Outfit']">Guests</h1>
          </div>
          <p className="text-gray-400 text-sm">
            Everyone who scanned your QR and verified their number. Export to CSV for outreach.
          </p>
        </div>

        {/* Toolbar */}
        <div className="flex items-center justify-between gap-3 mb-5 flex-wrap">
          <div className="inline-flex rounded-full bg-white/[0.04] border border-white/10 p-1 flex-wrap" data-testid="guests-range-tabs">
            {RANGE_OPTIONS.map((opt, i) => (
              <button
                key={opt.label}
                onClick={() => setRangeIdx(i)}
                className={`px-3 py-1.5 text-xs font-semibold rounded-full transition-all ${
                  rangeIdx === i
                    ? 'bg-[#FF007F] text-white'
                    : 'text-gray-400 hover:text-white'
                }`}
                data-testid={`guests-range-${opt.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {opt.label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <Button
              onClick={fetchGuests}
              disabled={loading}
              variant="ghost"
              size="sm"
              className="text-gray-400 hover:text-white"
              data-testid="guests-refresh-btn"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
            <Button
              onClick={downloadCsv}
              disabled={downloading || data.guests.length === 0}
              className="bg-[#FF007F] hover:bg-[#CC0066] text-white rounded-full h-9 px-4"
              data-testid="guests-download-csv-btn"
            >
              {downloading ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Preparing…</>
              ) : (
                <><Download className="w-4 h-4 mr-2" /> Download CSV</>
              )}
            </Button>
          </div>
        </div>

        {/* Summary cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-6" data-testid="guests-summary">
          <StatCard label="Total contacts" value={data.total} icon={<Users className="w-4 h-4" />} color="#FF007F" />
          <StatCard label="Verified guests" value={data.verified_count ?? 0} icon={<Users className="w-4 h-4" />} color="#39FF14" testid="guests-stat-verified" />
          <StatCard label="Leads" value={data.lead_count ?? 0} icon={<Phone className="w-4 h-4" />} color="#00F5FF" testid="guests-stat-leads" />
          <StatCard label="Song requests" value={data.total_requests} icon={<Music className="w-4 h-4" />} color="#9D00FF" />
        </div>

        {/* Table */}
        <div className="rounded-2xl bg-white/[0.03] border border-white/8 overflow-hidden" data-testid="guests-table-wrap">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-6 h-6 text-[#FF007F] animate-spin" />
            </div>
          ) : data.guests.length === 0 ? (
            <div className="text-center py-16 px-6">
              <Users className="w-10 h-10 text-gray-600 mx-auto mb-3" />
              <p className="text-white font-semibold">No guests yet</p>
              <p className="text-gray-500 text-sm mt-1">
                Once guests scan your QR and verify their number, they&apos;ll show up here.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-[0.2em] text-gray-500 font-['JetBrains_Mono'] border-b border-white/8">
                    <th className="px-4 py-3">Name</th>
                    <th className="px-4 py-3">Phone</th>
                    <th className="px-4 py-3">Type</th>
                    <th className="px-4 py-3 hidden sm:table-cell">First Scan</th>
                    <th className="px-4 py-3">Last Scan</th>
                    <th className="px-4 py-3 text-right">Scans</th>
                    <th className="px-4 py-3 text-right">Requests</th>
                  </tr>
                </thead>
                <tbody data-testid="guests-table-body">
                  {data.guests.map((g, i) => (
                    <tr
                      key={`${g.phone}-${i}`}
                      className="border-b border-white/5 last:border-0 hover:bg-white/[0.02]"
                      data-testid={`guest-row-${i}`}
                      data-verified={String(!!g.verified)}
                    >
                      <td className="px-4 py-3 text-white">{g.name || <span className="text-gray-600 italic">—</span>}</td>
                      <td className="px-4 py-3 text-gray-300 font-mono text-xs">
                        {g.country_code ? `${g.country_code} ` : ''}{g.phone}
                      </td>
                      <td className="px-4 py-3">
                        {g.verified ? (
                          <span
                            className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider"
                            style={{ background: 'rgba(57,255,20,0.1)', color: '#39FF14', border: '1px solid rgba(57,255,20,0.35)' }}
                            data-testid={`guest-badge-verified-${i}`}
                          >
                            Verified
                          </span>
                        ) : (
                          <span
                            className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider"
                            style={{ background: 'rgba(0,245,255,0.08)', color: '#7ff1ff', border: '1px solid rgba(0,245,255,0.3)' }}
                            data-testid={`guest-badge-lead-${i}`}
                          >
                            Lead
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-gray-400 text-xs hidden sm:table-cell">{formatDate(g.first_scan_at)}</td>
                      <td className="px-4 py-3 text-gray-400 text-xs">{formatDate(g.last_scan_at)}</td>
                      <td className="px-4 py-3 text-right text-gray-300">{g.scan_count}</td>
                      <td className="px-4 py-3 text-right">
                        {g.request_count > 0 ? (
                          <span className="inline-flex items-center gap-1 text-[#FF007F] font-semibold">
                            <Music className="w-3 h-3" /> {g.request_count}
                          </span>
                        ) : (
                          <span className="text-gray-600">0</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};


const StatCard = ({ label, value, icon, color, testid }) => (
  <div
    className="rounded-2xl border p-3 md:p-4"
    style={{
      background: `linear-gradient(135deg, ${color}10 0%, transparent 100%)`,
      borderColor: `${color}30`,
    }}
    data-testid={testid}
  >
    <div className="flex items-center gap-2 mb-1" style={{ color }}>
      {icon}
      <p className="text-[10px] uppercase tracking-[0.2em] font-['JetBrains_Mono']">{label}</p>
    </div>
    <p className="text-white text-2xl md:text-3xl font-bold font-['Outfit']">{value}</p>
  </div>
);


export default GuestsPage;
